/**
 * A clock that only runs when something is watching it.
 *
 * Home carries a live countdown in the last twenty-four hours before a deadline (Tim, 23 August).
 * That is one second-by-second repaint a year's worth of days — and a `setInterval(…, 1000)` left
 * running the other 364 is a repaint of the whole page every second, forever, on a phone in
 * somebody's pocket. So the interval is CONDITIONAL: `on` comes from `liveLeft().ticking`, which
 * is false until a deadline is inside a day, and the timer does not exist until then.
 *
 * `ms` is a parameter rather than a constant because the draft board wants a slower one — a pick
 * clock reads fine at 1000ms, the elapsed clock on a quiet page does not need better than 5000.
 */

import { useEffect, useState } from 'react';

export function useTick(on: boolean, ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return undefined;
    /*
     * Set once immediately: the component may have mounted a long time before `on` flipped, and
     * without this the first render after it flips shows a stale `now` for up to a second — which
     * on a countdown reads as the clock stalling at the moment it starts.
     */
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [on, ms]);
  return now;
}
